import { ExternalLink } from "lucide-react";
import { Badge } from "@baluu/ui/components/badge";
import { Button } from "@baluu/ui/components/button";
import { frequencyUsageLines } from "@baluu/ui/lib/memberships/format-benefit";
import { TabSkeleton, TabError } from "../TabStatus";
import { formatDateShort } from "../format";
import type { MembershipSubscription, MembershipSubscriptionStatus } from "../types";

/**
 * Ported from ProfileView.tsx's `MembershipCard` (:321-473).
 *
 * Omitted: the hosted version also renders `membership.businessCustomer?.tags`
 * as badges (ProfileView.tsx:413-418). `MembershipSubscription` here
 * (../types.ts, copied field-for-field from MeMemberships.controller.ts) does
 * NOT carry `businessCustomer` — that controller's `findMany` only
 * `include`s `membershipPlan` (types.ts:180-186), so the relation is never
 * fetched. There is no tags data to render; the badges are simply omitted
 * rather than invented.
 */

const STATUS_STYLES: Partial<Record<MembershipSubscriptionStatus, string>> = {
  active: "bg-green-100 text-green-700",
  trialing: "bg-blue-100 text-blue-700",
  past_due: "bg-yellow-100 text-yellow-700",
  paused: "bg-gray-100 text-gray-700",
  incomplete: "bg-orange-100 text-orange-700",
  scheduled: "bg-sky-100 text-sky-800",
};

function StatusBadge({ membership }: { membership: MembershipSubscription }) {
  if (membership.cancelAtPeriodEnd) {
    return (
      <Badge variant="destructive" className="text-xs">
        Cancelling
      </Badge>
    );
  }
  const style = STATUS_STYLES[membership.status] || "bg-gray-100 text-gray-700";
  const label =
    membership.status === "scheduled"
      ? `Starts ${formatDateShort(membership.currentPeriodStart)}`
      : membership.status === "past_due"
        ? "Past Due"
        : membership.status.charAt(0).toUpperCase() + membership.status.slice(1);
  return (
    <Badge variant="secondary" className={`text-xs ${style}`}>
      {label}
    </Badge>
  );
}

export function MembershipsTab({
  memberships,
  isLoading,
  isError,
  onRetry,
  onManage,
  isManaging,
  managingId,
  manageError,
}: {
  memberships: MembershipSubscription[];
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  onManage: (membershipSubscriptionId: string) => void;
  isManaging: boolean;
  managingId?: string;
  manageError?: boolean;
}) {
  if (isLoading) return <TabSkeleton rows={2} />;
  if (isError) return <TabError onRetry={onRetry} />;

  const activeMemberships = memberships.filter((m) => m.status !== "cancelled");
  if (activeMemberships.length === 0) {
    return <div className="py-12 text-center text-muted-foreground">You don&apos;t have any memberships yet.</div>;
  }

  return (
    <div className="space-y-4">
      {activeMemberships.map((membership) => {
        const plan = membership.membershipPlan;
        const isCredit = plan?.type === "credit";
        const creditsPerCycle = plan?.creditsPerCycle || 0;
        const currentCredits = membership.currentCredits || 0;
        const creditUsage = creditsPerCycle > 0 ? ((creditsPerCycle - currentCredits) / creditsPerCycle) * 100 : 0;

        let creditColor = "bg-green-500";
        if (currentCredits <= 1) creditColor = "bg-red-500";
        else if (currentCredits <= Math.ceil(creditsPerCycle * 0.3)) creditColor = "bg-yellow-500";

        const rowLoading = isManaging && managingId === membership.id;

        return (
          <div key={membership.id} className="space-y-2 rounded-lg bg-muted/30 p-3">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-medium">{plan?.name || "Membership"}</span>
                <Badge variant="outline" className="text-xs">
                  {isCredit ? "Credit" : plan?.type === "status" ? "Member" : "Unlimited"}
                </Badge>
                <StatusBadge membership={membership} />
              </div>
              <Button variant="outline" size="sm" onClick={() => onManage(membership.id)} disabled={rowLoading} className="flex items-center gap-1">
                <ExternalLink className="h-3 w-3" />
                {rowLoading ? "Loading..." : "Manage"}
              </Button>
            </div>

            {isCredit && creditsPerCycle > 0 && (
              <>
                <div className="flex items-center justify-between text-sm text-muted-foreground">
                  <span>
                    {currentCredits} of {creditsPerCycle} credits remaining
                  </span>
                </div>
                <div className="relative h-2 overflow-hidden rounded-full bg-muted">
                  <div className={`absolute left-0 top-0 h-full ${creditColor} transition-all`} style={{ width: `${100 - creditUsage}%` }} />
                </div>
              </>
            )}

            {frequencyUsageLines(membership).map((line) => (
              <p key={line} className="text-sm text-muted-foreground">
                {line}
              </p>
            ))}

            <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
              <span>
                Period: {formatDateShort(membership.currentPeriodStart)} - {formatDateShort(membership.currentPeriodEnd)}
              </span>
              <span>Next billing: {formatDateShort(membership.currentPeriodEnd)}</span>
            </div>

            {membership.cancelAtPeriodEnd && (
              <p className="text-xs font-medium text-destructive">
                Your membership will end on {formatDateShort(membership.currentPeriodEnd)}
              </p>
            )}
          </div>
        );
      })}
      {manageError && <p className="text-sm text-destructive">Couldn&apos;t open billing management. Please try again.</p>}
    </div>
  );
}
